/**
 * Find the earlier event whose end is closest to the selected event's start.
 * @param {Array<{start: number, end: number}>} events
 * @param {number} selectedIndex
 * @returns {{start: number, end: number} | null}
 */
function findClosestEarlierEvent(events, selectedIndex) {
    const selectedEvent = events[selectedIndex];
    if (!selectedEvent) {
        return null;
    }

    let closest = null;
    let closestDiff = Infinity;

    for (let index = 0; index < events.length; index++) {
        if (index === selectedIndex) {
            continue;
        }

        const event = events[index];
        if (!event || event.start >= selectedEvent.start || !(event.end > 0)) {
            continue;
        }

        const diff = Math.abs(event.end - selectedEvent.start);
        if (diff < closestDiff) {
            closest = event;
            closestDiff = diff;
        }
    }

    return closest;
}

/**
 * Find the later event whose start is closest to the selected event's end.
 * @param {Array<{start: number, end: number}>} events
 * @param {number} selectedIndex
 * @returns {{start: number, end: number} | null}
 */
function findClosestLaterEvent(events, selectedIndex) {
    const selectedEvent = events[selectedIndex];
    if (!selectedEvent) {
        return null;
    }

    let closest = null;
    let closestDiff = Infinity;

    for (let index = 0; index < events.length; index++) {
        if (index === selectedIndex) {
            continue;
        }

        const event = events[index];
        if (!event || event.end <= selectedEvent.end || !(event.start > 0)) {
            continue;
        }

        const diff = Math.abs(event.start - selectedEvent.end);
        if (diff < closestDiff) {
            closest = event;
            closestDiff = diff;
        }
    }

    return closest;
}

/**
 * Snap each selected event's start (incode) to the nearest earlier event's end (outcode).
 * Mutates `events` in place.
 * @param {Array<{start: number, end: number}>} events
 * @param {number[]} selectedIndexes
 * @param {number} gapSec
 * @returns {boolean} whether any event was changed
 */
export function snapSelectedStartsToPrevious(events, selectedIndexes, gapSec) {
    if (!Array.isArray(events) || !Array.isArray(selectedIndexes) || selectedIndexes.length === 0) {
        return false;
    }

    const gap = Number(gapSec) || 0;
    let changed = false;

    for (const selectedIndex of selectedIndexes) {
        const selectedEvent = events[selectedIndex];
        const closest = findClosestEarlierEvent(events, selectedIndex);
        if (!selectedEvent || !closest) {
            continue;
        }

        const nextStart = closest.end + gap;
        if (selectedEvent.start !== nextStart) {
            selectedEvent.start = nextStart;
            changed = true;
        }
    }

    return changed;
}

/**
 * Snap each selected event's end (outcode) to the nearest later event's start (incode).
 * Mutates `events` in place.
 * @param {Array<{start: number, end: number}>} events
 * @param {number[]} selectedIndexes
 * @param {number} gapSec
 * @returns {boolean} whether any event was changed
 */
export function snapSelectedEndsToNext(events, selectedIndexes, gapSec) {
    if (!Array.isArray(events) || !Array.isArray(selectedIndexes) || selectedIndexes.length === 0) {
        return false;
    }

    const gap = Number(gapSec) || 0;
    let changed = false;

    for (const selectedIndex of selectedIndexes) {
        const selectedEvent = events[selectedIndex];
        const closest = findClosestLaterEvent(events, selectedIndex);
        if (!selectedEvent || !closest) {
            continue;
        }

        const nextEnd = closest.start - gap;
        if (selectedEvent.end !== nextEnd) {
            selectedEvent.end = nextEnd;
            changed = true;
        }
    }

    return changed;
}
